"use client";

import { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const {
    ref: lineRef,
    isVisible: lineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [buttonHovered, setButtonHovered] = useState(false);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let width = 0;
    let height = 0;
    const resize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    type Trail = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      length: number;
      opacity: number;
      hue: number;
      width: number;
      life: number;
      maxLife: number;
    };
    const trails: Trail[] = [];
    const spawnTrail = () => {
      const fromLeft = Math.random() > 0.5;
      const x = fromLeft ? -100 : width + 100;
      const y = Math.random() * height;
      const speed = 2 + Math.random() * 4;
      const vx = fromLeft ? speed : -speed;
      const vy = (Math.random() - 0.5) * 0.5;
      const maxLife = 120 + Math.random() * 80;
      trails.push({
        x,
        y,
        vx,
        vy,
        length: 80 + Math.random() * 160,
        opacity: 0.1 + Math.random() * 0.2,
        hue: 221,
        width: 0.5 + Math.random() * 1,
        life: 0,
        maxLife
      });
    };
    let frame = 0;
    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      frame++;
      if (frame % 25 === 0 && trails.length < 15) {
        spawnTrail();
      }
      for (let i = trails.length - 1; i >= 0; i--) {
        const t = trails[i];
        t.x += t.vx;
        t.y += t.vy;
        t.life++;
        const lifeRatio = t.life / t.maxLife;
        const fade = lifeRatio < 0.2 ? lifeRatio / 0.2 : lifeRatio > 0.8 ? (1 - lifeRatio) / 0.2 : 1;
        const grad = ctx.createLinearGradient(t.x - t.vx * (t.length / Math.abs(t.vx)), t.y, t.x, t.y);
        grad.addColorStop(0, `hsla(${t.hue}, 83%, 45%, 0)`);
        grad.addColorStop(1, `hsla(${t.hue}, 83%, 45%, ${t.opacity * fade})`);
        ctx.beginPath();
        ctx.moveTo(t.x - t.vx * (t.length / Math.abs(t.vx)), t.y);
        ctx.lineTo(t.x, t.y);
        ctx.strokeStyle = grad;
        ctx.lineWidth = t.width;
        ctx.stroke();
        if (t.life >= t.maxLife) {
          trails.splice(i, 1);
        }
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };
    const waitReady = () => {
      if (canvas.offsetWidth > 0 && canvas.offsetHeight > 0) {
        resize();
        animate();
      } else {
        setTimeout(waitReady, 50);
      }
    };
    waitReady();
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <section data-section-id="231218" id="intro-hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-20" style={{
      backgroundImage: `linear-gradient(to right, hsl(221 83% 90%) 1px, transparent 1px), linear-gradient(to bottom, hsl(221 83% 90%) 1px, transparent 1px)`,
      backgroundSize: '40px 40px'
    }} />

      {}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{
      opacity: 0.4
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(circle at 50% 50%, hsla(221, 83%, 45%, 0.03) 0%, transparent 70%)"
    }} />

      {}
      <div ref={contentRef} className="relative z-10 flex flex-col items-center text-center px-6 py-20 max-w-5xl mx-auto">
        {}
        <div className="inline-flex items-center gap-2 rounded-none px-4 py-1.5 mb-8 border text-sm font-mono font-semibold tracking-widest uppercase" style={{
        backgroundColor: "transparent",
        borderColor: "var(--primary)",
        color: "var(--primary)",
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.6s ease, transform 0.6s ease"
      }}>
          <span className="inline-block w-2 h-2 rounded-none" style={{
          backgroundColor: "var(--primary)",
          animation: "pulse 2s ease-in-out infinite"
        }} />
          Citizen Advocates Network
        </div>

        {}
        <h1 className="font-default font-extrabold leading-none mb-6" style={{
        fontSize: "clamp(3rem, 10vw, 82pt)",
        letterSpacing: "-0.04em",
        color: "var(--foreground)",
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? "translateY(0)" : "translateY(30px)",
        transition: "opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s"
      }}>
          Insights for Action.
        </h1>

        {}
        <p className="font-default text-lg md:text-2xl font-medium mb-3 max-w-2xl" style={{
        color: "var(--foreground)",
        opacity: contentVisible ? 0.95 : 0,
        transform: contentVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s"
      }}>
          Empowering citizens through a data-driven approach to influence governments.
        </p>

        <p className="font-default text-base md:text-lg mb-10 max-w-xl" style={{
        color: "var(--muted-foreground)",
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.7s ease 0.42s, transform 0.7s ease 0.42s"
      }}>
          We help governments be responsive by providing transparent research and actionable intelligence.
        </p>

        {}
        <div ref={lineRef} className="w-full max-w-xs h-px mb-10 overflow-hidden" style={{
        background: "var(--border)"
      }}>
          <div style={{
          height: "1px",
          background: "var(--primary)",
          width: lineVisible ? "100%" : "0%",
          transition: "width 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.2s"
        }} />
        </div>

        {}
        <div style={{
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.7s ease 0.55s, transform 0.7s ease 0.55s"
      }}>
          <Button asChild className="rounded-sm font-default font-bold text-lg px-10 py-6 h-auto border-0" onMouseEnter={() => setButtonHovered(true)} onMouseLeave={() => setButtonHovered(false)} style={{
          backgroundColor: "var(--primary)",
          color: "var(--primary-foreground)",
          boxShadow: buttonHovered ? "0 10px 25px -5px hsla(221, 83%, 45%, 0.3)" : "none",
          transition: "all 0.3s ease",
          transform: buttonHovered ? "translateY(-2px)" : "translateY(0)"
        }}>
            <Link to="#skill-building-laboratories" onClick={e => {
            e.preventDefault();
            document.querySelector('#skill-building-laboratories')?.scrollIntoView({
              behavior: 'smooth'
            });
          }}>
              Browse by Category
            </Link>
          </Button>
        </div>

        {}
        <div className="mt-16 flex flex-col items-center gap-3" style={{
        opacity: contentVisible ? 1 : 0,
        transition: "opacity 0.8s ease 0.8s"
      }}>
          
          
        </div>
      </div>

      {}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.2); }
        }
        @keyframes waveBar {
          0% { transform: scaleY(0.6); opacity: 0.2; }
          100% { transform: scaleY(1.4); opacity: 0.6; }
        }
      `}</style>
    </section>;
}